/**
 * ForecastPage — the "econ professor's" profit & inventory model.
 *
 * The backend (/api/admin/forecast) supplies only hard figures per variant:
 * sell price, stored COGS, units on hand, units sold, and a sales-velocity
 * window. EVERY cost assumption below — shipping, packaging, custom labeling,
 * misc, payment processing — is owned by the admin and applied client-side, so
 * the whole contribution-margin model is fully customizable with no schema or
 * deploy needed. Assumptions persist to localStorage so a reload keeps your model.
 *
 * Contribution margin per unit =
 *   price − (COGS + shipping + packaging + labeling + misc + payment processing)
 *
 * All money is USD cents internally (matching the rest of the back office); the
 * cost inputs are entered in dollars and converted. `fmt` renders USD.
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import { useCurrency } from '../../hooks/useCurrency';
import { toast } from 'react-toastify';

const LS_ASSUMPTIONS = 'tt.forecast.assumptions.v1';
const LS_COGS_OVERRIDES = 'tt.forecast.cogsOverrides.v1';
const LS_ALLIN_OVERRIDES = 'tt.forecast.allInOverrides.v1';

// All per-unit costs are in DOLLARS as entered by the admin; processingPct is a
// percentage of the sell price; processingFlat is dollars per unit sold.
const DEFAULTS = {
  shipping: 0,
  packaging: 0,
  labeling: 0,
  misc: 0,
  processingPct: 2.9,
  processingFlat: 0.30,
  globalCogs: 0,   // fallback COGS (in $) when a variant has no stored cost
  horizon: 90,     // projection horizon in days
};

function loadJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
}

const num = (v) => (v === '' || v == null || isNaN(Number(v)) ? 0 : Number(v));

// ─── Cost-assumption input field ──────────────────────────────────────────────
function CostField({ label, value, onChange, prefix, suffix, hint }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <div className="flex items-center rounded-lg border border-gray-300 focus-within:border-tea-400 bg-white overflow-hidden">
        {prefix && <span className="pl-2 text-gray-400 text-sm">{prefix}</span>}
        <input
          type="number"
          step="0.01"
          min="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-2 py-1.5 text-sm focus:outline-none"
        />
        {suffix && <span className="pr-2 text-gray-400 text-sm">{suffix}</span>}
      </div>
      {hint && <p className="text-[10px] text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function KpiCard({ label, value, sub, tone }) {
  const toneCls =
    tone === 'good' ? 'text-blue-600' :
    tone === 'bad' ? 'text-red-600' :
    'text-gray-900';
  return (
    <div className="bg-white rounded-xl border p-5 shadow-sm">
      <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{label}</div>
      <div className={'text-2xl font-bold mt-1 ' + toneCls}>{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function ForecastPage() {
  const { fmt } = useCurrency({ usd: true });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [a, setA] = useState(() => loadJSON(LS_ASSUMPTIONS, DEFAULTS));
  const [overrides, setOverrides] = useState(() => loadJSON(LS_COGS_OVERRIDES, {}));
  const [allInOverrides, setAllInOverrides] = useState(() => loadJSON(LS_ALLIN_OVERRIDES, {}));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/admin/forecast', { params: { days: a.horizon } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => toast.error('Failed to load forecast data'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [a.horizon]);

  useEffect(() => { try { localStorage.setItem(LS_ASSUMPTIONS, JSON.stringify(a)); } catch {} }, [a]);
  useEffect(() => { try { localStorage.setItem(LS_COGS_OVERRIDES, JSON.stringify(overrides)); } catch {} }, [overrides]);
  useEffect(() => { try { localStorage.setItem(LS_ALLIN_OVERRIDES, JSON.stringify(allInOverrides)); } catch {} }, [allInOverrides]);

  const setField = (k) => (v) => setA((s) => ({ ...s, [k]: v === '' ? '' : Number(v) }));

  // The model: convert dollar assumptions to cents, compute per-variant economics.
  const model = useMemo(() => {
    if (!data) return null;
    const days = data.days || 90;
    const horizon = num(a.horizon);

    // Per-unit fixed cost adders, in cents.
    const addersCents = (num(a.shipping) + num(a.packaging) + num(a.labeling) + num(a.misc)) * 100;
    const procFlatCents = num(a.processingFlat) * 100;
    const procPct = num(a.processingPct) / 100;
    const globalCogsCents = num(a.globalCogs) * 100;

    const rows = (data.variants || []).map((v) => {
      const priceCents = num(v.priceInCents);
      const inventory = num(v.inventory);

      // COGS resolution: per-variant override (in $) → stored costInCents → global default.
      const ov = overrides[v.id];
      const cogsCents =
        ov !== undefined && ov !== '' ? num(ov) * 100 :
        v.costInCents != null ? num(v.costInCents) :
        globalCogsCents;

      const processingCents = priceCents * procPct + procFlatCents;
      const computedUnitCostCents = cogsCents + addersCents + processingCents;
      // A direct all-in cost override (entered in $) replaces the whole computed buildup for this row.
      const allInOv = allInOverrides[v.id];
      const allInOverridden = allInOv !== undefined && allInOv !== '';
      const unitCostCents = allInOverridden ? num(allInOv) * 100 : computedUnitCostCents;
      const unitMarginCents = priceCents - unitCostCents;
      const marginPct = priceCents > 0 ? (unitMarginCents / priceCents) * 100 : 0;

      const velocity = days > 0 ? num(v.unitsSoldWindow) / days : 0; // units/day
      const daysToSellOut = velocity > 0 ? inventory / velocity : null;
      const projectedUnits = Math.min(inventory, velocity * horizon);

      return {
        ...v,
        priceCents,
        inventory,
        cogsCents,
        unitCostCents,
        computedUnitCostCents,
        allInOverridden,
        unitMarginCents,
        marginPct,
        velocity,
        daysToSellOut,
        projectedUnits,
        potentialRevenueCents: priceCents * inventory,
        inventoryCostCents: unitCostCents * inventory,
        profitIfSoldOutCents: unitMarginCents * inventory,
        projectedRevenueCents: priceCents * projectedUnits,
        projectedProfitCents: unitMarginCents * projectedUnits,
      };
    });

    const sum = (f) => rows.reduce((t, r) => t + f(r), 0);
    return {
      rows,
      totals: {
        unitsInStock: sum((r) => r.inventory),
        potentialRevenueCents: sum((r) => r.potentialRevenueCents),
        inventoryCostCents: sum((r) => r.inventoryCostCents),
        profitIfSoldOutCents: sum((r) => r.profitIfSoldOutCents),
        projectedUnits: sum((r) => r.projectedUnits),
        projectedRevenueCents: sum((r) => r.projectedRevenueCents),
        projectedProfitCents: sum((r) => r.projectedProfitCents),
      },
    };
  }, [data, a, overrides, allInOverrides]);

  if (loading && !data) {
    return <div className="py-20 text-center text-gray-400">Loading forecast…</div>;
  }
  if (!model) {
    return <div className="py-20 text-center text-gray-400">No data available.</div>;
  }

  const t = model.totals;
  const blendedMargin = t.potentialRevenueCents > 0
    ? (t.profitIfSoldOutCents / t.potentialRevenueCents) * 100
    : 0;

  return (
    <div>
      <div className="mb-2">
        <h1 className="text-3xl font-bold">Profit Forecast</h1>
        <p className="text-sm text-gray-500 mt-1 max-w-3xl">
          A live contribution-margin model. Per unit,{' '}
          <span className="font-semibold text-gray-700">
            margin = price − (COGS + shipping + packaging + labeling + misc + payment processing)
          </span>
          . Set your cost assumptions below — every figure recomputes instantly. Sales velocity and
          the inventory sell-through projection are derived from your real order data
          (paid / processing / shipped / delivered).
        </p>
      </div>

      {/* ─── Cost assumptions ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border p-5 shadow-sm mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="font-bold text-lg">
            Cost assumptions
            <span className="ml-2 text-xs font-normal text-gray-400">saved on this device</span>
          </h2>
          <button
            onClick={() => { setA(DEFAULTS); setOverrides({}); setAllInOverrides({}); toast.success('Assumptions reset'); }}
            className="text-xs font-medium text-tea-600 hover:text-tea-700"
          >
            Reset to defaults
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <CostField label="Shipping / unit"   prefix="$" value={a.shipping}       onChange={setField('shipping')} />
          <CostField label="Packaging / unit"  prefix="$" value={a.packaging}      onChange={setField('packaging')} />
          <CostField label="Labeling / unit"   prefix="$" value={a.labeling}       onChange={setField('labeling')} />
          <CostField label="Misc / unit"       prefix="$" value={a.misc}           onChange={setField('misc')} />
          <CostField label="Processing"        suffix="%" value={a.processingPct}  onChange={setField('processingPct')} hint="of sell price" />
          <CostField label="Processing flat"   prefix="$" value={a.processingFlat} onChange={setField('processingFlat')} hint="per unit sold" />
          <CostField label="Default COGS"      prefix="$" value={a.globalCogs}     onChange={setField('globalCogs')} hint="when none stored" />
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Projection horizon</label>
            <select
              value={a.horizon}
              onChange={(e) => setField('horizon')(e.target.value)}
              className="w-full px-2 py-1.5 text-sm rounded-lg border border-gray-300 bg-white focus:outline-none focus:border-tea-400"
            >
              {[30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>{d} days</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ─── Headline KPIs ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <KpiCard
          label="Profit if ALL stock sold"
          value={fmt(t.profitIfSoldOutCents)}
          tone={t.profitIfSoldOutCents >= 0 ? 'good' : 'bad'}
          sub="At your current cost model"
        />
        <KpiCard label="Potential revenue" value={fmt(t.potentialRevenueCents)} sub="Stock × sell price" />
        <KpiCard label="Inventory cost to fulfill" value={fmt(t.inventoryCostCents)} sub="All-in cost of stock on hand" />
        <KpiCard label="Blended gross margin" value={blendedMargin.toFixed(1) + '%'} />
        <KpiCard label="Units in stock" value={Number(t.unitsInStock).toLocaleString()} />
      </div>

      {/* ─── Inventory sell-through projection ───────────────────────────── */}
      <div className="bg-white rounded-xl border p-5 shadow-sm mb-6">
        <h2 className="font-bold text-lg mb-1">Inventory projection — next {a.horizon} days</h2>
        <p className="text-xs text-gray-400 mb-4">
          Velocity is measured over the last {data.days} days of real orders, then projected forward
          and capped by units on hand.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-lg bg-gray-50 border border-gray-100 p-4">
            <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Projected units sold</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{Math.round(t.projectedUnits).toLocaleString()}</div>
          </div>
          <div className="rounded-lg bg-gray-50 border border-gray-100 p-4">
            <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Projected revenue</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{fmt(t.projectedRevenueCents)}</div>
          </div>
          <div className="rounded-lg bg-blue-50 border border-blue-100 p-4">
            <div className="text-[11px] uppercase tracking-wide text-blue-700 font-semibold">Projected profit</div>
            <div className={'text-2xl font-bold mt-1 ' + (t.projectedProfitCents >= 0 ? 'text-blue-700' : 'text-red-600')}>
              {fmt(t.projectedProfitCents)}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Per-variant breakdown ───────────────────────────────────────── */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-bold text-lg">Per-product breakdown</h2>
          <span className="text-xs text-gray-400">
            COGS blank = stored/default cost. All-in cost blank = computed from your assumptions; type a value to override the whole per-unit cost.
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Product / Variant', 'Price', 'COGS / unit', 'All-in cost', 'Margin / unit',
                  'Margin %', 'Stock', 'Profit if sold out', 'Velocity /day', 'Sells out in',
                  `Proj. profit (${a.horizon}d)`].map((h, i) => (
                  <th key={h} className={'px-3 py-2.5 font-semibold text-gray-600 whitespace-nowrap ' + (i === 0 ? 'text-left' : 'text-right')}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {model.rows.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-gray-400">No products yet.</td></tr>
              )}
              {model.rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-gray-900">{r.productName}</div>
                    <div className="text-xs text-gray-400">
                      {r.variantName}
                      {!r.isActive && <span className="ml-1 text-gray-300">(inactive)</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmt(r.priceCents)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="inline-flex items-center rounded border border-gray-200 focus-within:border-tea-400 overflow-hidden">
                      <span className="pl-1.5 text-gray-400 text-xs">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={r.costInCents != null ? (r.costInCents / 100).toFixed(2) : (num(a.globalCogs)).toFixed(2)}
                        value={overrides[r.id] != null ? overrides[r.id] : ''}
                        onChange={(e) =>
                          setOverrides((o) => ({ ...o, [r.id]: e.target.value === '' ? '' : Number(e.target.value) }))
                        }
                        className="w-16 px-1 py-1 text-xs text-right focus:outline-none"
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className={'inline-flex items-center rounded border overflow-hidden focus-within:border-tea-400 ' + (r.allInOverridden ? 'border-tea-300 bg-tea-50' : 'border-gray-200')}>
                      <span className="pl-1.5 text-gray-400 text-xs">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        title={r.allInOverridden ? 'Manual override — clear to use the computed buildup (' + fmt(r.computedUnitCostCents) + ')' : 'Computed from your assumptions: ' + fmt(r.computedUnitCostCents) + '. Type to override.'}
                        placeholder={(r.computedUnitCostCents / 100).toFixed(2)}
                        value={allInOverrides[r.id] != null ? allInOverrides[r.id] : ''}
                        onChange={(e) =>
                          setAllInOverrides((o) => ({ ...o, [r.id]: e.target.value === '' ? '' : Number(e.target.value) }))
                        }
                        className="w-16 px-1 py-1 text-xs text-right focus:outline-none bg-transparent"
                      />
                    </div>
                  </td>
                  <td className={'px-3 py-2.5 text-right tabular-nums font-medium ' + (r.unitMarginCents >= 0 ? 'text-blue-700' : 'text-red-600')}>
                    {fmt(r.unitMarginCents)}
                  </td>
                  <td className={'px-3 py-2.5 text-right tabular-nums ' + (r.marginPct >= 0 ? 'text-blue-700' : 'text-red-600')}>
                    {r.marginPct.toFixed(1)}%
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{Number(r.inventory).toLocaleString()}</td>
                  <td className={'px-3 py-2.5 text-right tabular-nums font-medium ' + (r.profitIfSoldOutCents >= 0 ? 'text-blue-700' : 'text-red-600')}>
                    {fmt(r.profitIfSoldOutCents)}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">
                    {r.velocity > 0 ? r.velocity.toFixed(2) : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">
                    {r.daysToSellOut == null
                      ? <span className="text-gray-300">no sales</span>
                      : Math.ceil(r.daysToSellOut).toLocaleString() + 'd'}
                  </td>
                  <td className={'px-3 py-2.5 text-right tabular-nums font-medium ' + (r.projectedProfitCents >= 0 ? 'text-blue-700' : 'text-red-600')}>
                    {fmt(r.projectedProfitCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
